<template>
  <div class="alerts-feed">
    <div class="alerts-feed__toolbar">
      <div class="alerts-feed__seg" role="group" aria-label="Status">
        <button
          v-for="seg in SEGMENTS"
          :key="seg.value"
          type="button"
          class="alerts-feed__seg-btn"
          :class="{ 'is-on': filters.status === seg.value }"
          :aria-pressed="filters.status === seg.value ? 'true' : 'false'"
          @click="setFilter({ status: seg.value })"
        >{{ seg.label }}<em v-if="segmentCount(seg.value) !== null">{{ segmentCount(seg.value) }}</em></button>
      </div>
      <select :value="filters.severity" class="alerts-feed__sel" aria-label="Severity" @change="setFilter({ severity: $event.target.value })">
        <option value="">All severities</option>
        <option value="critical">Critical</option>
        <option value="serious">Serious</option>
        <option value="warning">Warning</option>
        <option value="info">Info</option>
      </select>
      <select :value="filters.rule_type" class="alerts-feed__sel" aria-label="Rule type" @change="setFilter({ rule_type: $event.target.value })">
        <option value="">All rule types</option>
        <option v-for="type in RULE_TYPES" :key="type" :value="type">{{ ruleTypeShortTitle(type) }}</option>
      </select>
      <select :value="filters.server_id" class="alerts-feed__sel" aria-label="Server" @change="setFilter({ server_id: $event.target.value })">
        <option value="">All servers</option>
        <option v-for="server in serverOptions" :key="server.id" :value="server.id">{{ server.label }}</option>
      </select>
      <span class="alerts-feed__spacer" />
      <span class="alerts-feed__note">{{ countingNote }}</span>
    </div>

    <div v-if="filters.since || filters.until" class="alerts-feed__scope-chip">
      Showing alerts open between {{ windowLabel(filters.since) }} and {{ windowLabel(filters.until) }}
      <button type="button" class="alerts-admin-board__link-btn" @click="setFilter({ since: '', until: '' })">Clear</button>
    </div>

    <div v-if="loadError" class="alerts-feed__error" role="status">{{ loadError }}</div>

    <div v-if="loading && !loadedOnce" class="alerts-feed__empty">Loading the feed…</div>

    <template v-else>
      <div v-if="isEmpty" class="alerts-feed__empty-state" :class="{ 'is-positive': positiveEmpty }">
        <template v-if="positiveEmpty">
          <div class="alerts-feed__es-glyph"><AlertsIcon name="check" /></div>
          <h4>All clear: nothing needs action</h4>
          <p>{{ positiveEmptyDetail }}</p>
        </template>
        <p v-else>{{ emptyText }}</p>
      </div>

      <template v-for="section in sections" :key="section.key">
        <div v-if="section.title" class="alerts-feed__section">{{ section.title }} <span>· {{ section.rows.length }}</span></div>
        <AlertsFeedRow
          v-for="row in section.rows"
          :key="row.id"
          :ref="(el) => registerRow(row.id, el)"
          :row="row"
          :now="now"
          :expanded="expanded.has(String(row.id))"
          :highlighted="String(row.id) === String(filters.alert || '')"
          :error="rowErrors[row.id] || ''"
          :busy="busy.has(String(row.id))"
          @toggle="toggle(row)"
          @ack="ack(row)"
          @resolve="resolve(row)"
          @snooze="openSnooze(row)"
          @accept="openAccept(row)"
          @cancel-snooze="cancelSnooze(row)"
          @open-tab="(tab, query) => $emit('open-tab', tab, query)"
          @focus-row="focusedId = String(row.id)"
        />
      </template>

      <div v-if="foldRows.length" class="alerts-feed__fold">
        <button type="button" class="alerts-feed__fold-btn" :aria-expanded="showResolved ? 'true' : 'false'" @click="showResolved = !showResolved">
          <AlertsIcon :name="showResolved ? 'chev' : 'chevr'" />
          Resolved in the last 24 hours: <strong>{{ foldRows.length }}</strong>
          <span v-if="foldSummary" class="alerts-feed__fold-sum">({{ foldSummary }})</span>
          · {{ showResolved ? 'Hide' : 'Show' }}
        </button>
      </div>
      <template v-if="showResolved">
        <AlertsFeedRow
          v-for="row in foldRows"
          :key="row.id"
          :ref="(el) => registerRow(row.id, el)"
          :row="row"
          :now="now"
          :expanded="expanded.has(String(row.id))"
          :highlighted="String(row.id) === String(filters.alert || '')"
          @toggle="toggle(row)"
          @open-tab="(tab, query) => $emit('open-tab', tab, query)"
          @focus-row="focusedId = String(row.id)"
        />
      </template>

      <div v-if="hasMore" class="alerts-feed__more">
        <button type="button" class="alerts-admin-board__mini-btn" :disabled="loadingMore" @click="loadMore">{{ loadingMore ? 'Loading…' : 'Load more' }}</button>
      </div>
    </template>

    <AlertSnoozeModal v-if="snoozeFor" :alert="snoozeFor" :api-base="apiBase" @close="snoozeFor = null" @done="afterHold" />
    <AlertAcceptModal v-if="acceptFor" :alert="acceptFor" :api-base="apiBase" @close="acceptFor = null" @done="afterHold" />
    <AlertsDialog v-if="helpOpen" title="Keyboard shortcuts" @close="helpOpen = false">
      <dl class="alerts-feed__keys">
        <div><dt><kbd>j</kbd> / <kbd>k</kbd></dt><dd>Next / previous alert</dd></div>
        <div><dt><kbd>Enter</kbd></dt><dd>Expand or collapse the alert</dd></div>
        <div><dt><kbd>a</kbd></dt><dd>Acknowledge</dd></div>
        <div><dt><kbd>s</kbd></dt><dd>Snooze…</dd></div>
        <div><dt><kbd>x</kbd></dt><dd>Accept…</dd></div>
        <div><dt><kbd>?</kbd></dt><dd>This list</dd></div>
      </dl>
      <p class="alerts-accept__hint">Shortcuts are off while a dialog is open or you are typing in a field. Arrow keys move between the tabs.</p>
      <template #actions>
        <button type="button" class="alerts-admin-board__mini-btn alerts-admin-board__mini-btn--primary" @click="helpOpen = false">Close</button>
      </template>
    </AlertsDialog>
  </div>
</template>

<script setup>
import { computed, nextTick, onBeforeUnmount, onMounted, reactive, ref, watch } from 'vue';
import useApi from '@apexl/icarus-core/src/services/api';
import AlertsDialog from '@root/components/Alerts/AlertsDialog.vue';
import AlertsFeedRow from '@root/components/Alerts/AlertsFeedRow.vue';
import AlertsIcon from '@root/components/Alerts/AlertsIcon.vue';
import AlertAcceptModal from '@root/components/Alerts/AlertAcceptModal.vue';
import AlertSnoozeModal from '@root/components/Alerts/AlertSnoozeModal.vue';
import { parseMaybeJson, RULE_TYPES, ruleTypeShortTitle } from '@root/services/alertFormat';

// The Feed tab (design brief §2.3, Phase 2 slice). Rows are ALERT ROWS —
// episode grouping, the flapping row and the open/closed strip are Plan 012
// Phase 3. The default view is Active (open + acknowledged + snoozed) with
// the last 24 hours of resolved alerts folded into one row (brief Q3).
// Filters are controlled by the workspace (it writes them to the URL); this
// component asks for changes with `update:filters`.
const SEGMENTS = [
  { value: '', label: 'Active' },
  { value: 'needs_action', label: 'Open' },
  { value: 'acknowledged', label: 'Acknowledged' },
  { value: 'snoozed', label: 'Snoozed' },
  { value: 'resolved', label: 'Resolved' },
  { value: 'all', label: 'All' },
];
const SEVERITY_RANK = { critical: 0, serious: 1, warning: 2, info: 3 };
const POLL_MS = 20000;

const props = defineProps({
  apiBase: { type: String, required: true },
  filters: { type: Object, required: true },
  active: { type: Boolean, default: true },
});
const emit = defineEmits(['update:filters', 'summary', 'open-tab']);

const api = useApi();
const alerts = ref([]);
const summary = ref({});
const loading = ref(false);
const loadedOnce = ref(false);
const loadError = ref('');
const hasMore = ref(false);
const cursor = ref(null);
const loadingMore = ref(false);
const pinned = ref(null);
const now = ref(Math.floor(Date.now() / 1000));
const expanded = reactive(new Set());
const busy = reactive(new Set());
const rowErrors = reactive({});
const showResolved = ref(false);
const snoozeFor = ref(null);
const acceptFor = ref(null);
const helpOpen = ref(false);
const focusedId = ref('');
const rowEls = new Map();
let pollHandle = null;
let lastGoodAt = null;
let pendingScroll = '';

function normalise(a) {
  return { ...a, context: parseMaybeJson(a.context, null) };
}

const allRows = computed(() => {
  const list = [...alerts.value];
  const p = pinned.value;
  if (p && !list.some((a) => a.id === p.id)) list.push(p);
  return list;
});

function byPriority(a, b) {
  const s = (SEVERITY_RANK[a.severity] ?? 9) - (SEVERITY_RANK[b.severity] ?? 9);
  return s !== 0 ? s : Number(b.id) - Number(a.id);
}

const isActiveView = computed(() => (props.filters.status || '') === '');

const sections = computed(() => {
  if (!isActiveView.value) {
    const rows = [...allRows.value].sort((a, b) => Number(b.id) - Number(a.id));
    return rows.length ? [{ key: 'all', title: '', rows }] : [];
  }
  const live = allRows.value.filter((a) => a.status !== 'resolved');
  const needs = live.filter((a) => a.status === 'open' && !a.snooze).sort(byPriority);
  const acked = live.filter((a) => a.status === 'acknowledged' && !a.snooze).sort(byPriority);
  const snoozed = live.filter((a) => a.snooze).sort(byPriority);
  return [
    { key: 'needs', title: 'Needs action', rows: needs },
    { key: 'acked', title: 'Acknowledged', rows: acked },
    { key: 'snoozed', title: 'Snoozed', rows: snoozed },
  ].filter((s) => s.rows.length);
});

const foldRows = computed(() => (isActiveView.value
  ? allRows.value.filter((a) => a.status === 'resolved').sort((a, b) => Number(b.id) - Number(a.id))
  : []));

const foldSummary = computed(() => {
  const counts = {};
  foldRows.value.forEach((r) => {
    const t = r.short_title || ruleTypeShortTitle(r.rule_type);
    counts[t] = (counts[t] || 0) + 1;
  });
  return Object.entries(counts).sort((a, b) => b[1] - a[1]).slice(0, 4).map(([t, n]) => `${t} ×${n}`).join(', ');
});

const visibleRows = computed(() => [...sections.value.flatMap((s) => s.rows), ...(showResolved.value ? foldRows.value : [])]);

const hasNarrowing = computed(() => !!(props.filters.severity || props.filters.rule_type || props.filters.server_id || props.filters.since || props.filters.until));
const isEmpty = computed(() => loadedOnce.value && !sections.value.length);
const positiveEmpty = computed(() => isActiveView.value && !hasNarrowing.value);
const positiveEmptyDetail = computed(() => {
  const bits = [];
  if (summary.value.resolved_24h !== undefined) bits.push(`${summary.value.resolved_24h} resolved in the last 24 hours`);
  if (summary.value.snoozed) bits.push(`${summary.value.snoozed} snoozed`);
  return bits.join(' · ');
});
const emptyText = computed(() => {
  const label = {
    needs_action: 'No open alerts.', acknowledged: 'No acknowledged alerts.', snoozed: 'No snoozed alerts.', resolved: 'No resolved alerts.', all: 'No alerts.',
  }[props.filters.status || ''] || 'Nothing needs action.';
  return hasNarrowing.value ? `${label.replace(/\.$/, '')} match these filters.` : label;
});

const countingNote = computed(() => 'counted in alerts · episodes arrive with Phase 3');

function segmentCount(value) {
  const s = summary.value;
  switch (value) {
    case 'needs_action': return s.needs_action ?? null;
    case 'acknowledged': return s.acknowledged ?? null;
    case 'snoozed': return s.snoozed ?? null;
    case '': return s.needs_action !== undefined ? (s.needs_action + (s.acknowledged || 0) + (s.snoozed || 0)) : null;
    default: return null;
  }
}

const serverOptions = computed(() => {
  const map = new Map();
  allRows.value.forEach((a) => {
    if (a.server_id) map.set(String(a.server_id), a.subject || `server #${a.server_id}`);
  });
  if (props.filters.server_id && !map.has(String(props.filters.server_id))) {
    map.set(String(props.filters.server_id), `server #${props.filters.server_id}`);
  }
  return [...map.entries()].map(([id, label]) => ({ id, label })).sort((a, b) => a.label.localeCompare(b.label));
});

function windowLabel(epoch) {
  if (!epoch) return 'now';
  const date = new Date(Number(epoch) * 1000);
  return Number.isNaN(date.getTime()) ? '?' : date.toLocaleString(undefined, { weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
}

function setFilter(patch) {
  emit('update:filters', patch);
}

function queryString(extra = {}) {
  const params = new URLSearchParams();
  ['status', 'severity', 'server_id', 'rule_type', 'since', 'until'].forEach((key) => {
    if (props.filters[key]) params.set(key, props.filters[key]);
  });
  Object.entries(extra).forEach(([k, v]) => params.set(k, String(v)));
  return params.toString();
}

async function load() {
  loading.value = true;
  try {
    const qs = queryString();
    const data = await api.get(`${props.apiBase}/alerts${qs ? `?${qs}` : ''}`);
    const fresh = (data.alerts || []).map(normalise);
    const expandedPages = cursor.value !== null && alerts.value.length > fresh.length;
    if (expandedPages && data.has_more && fresh.length) {
      const lastFresh = Number(fresh[fresh.length - 1].id);
      alerts.value = [...fresh, ...alerts.value.filter((a) => Number(a.id) < lastFresh)];
    } else {
      alerts.value = fresh;
      hasMore.value = Boolean(data.has_more);
      cursor.value = data.next_before_id ?? null;
    }
    summary.value = data.summary || {};
    emit('summary', summary.value);
    loadError.value = '';
    lastGoodAt = new Date();
    loadedOnce.value = true;
    now.value = Math.floor(Date.now() / 1000);
    await ensurePinned();
    scrollToHighlight();
  } catch {
    // Keep the last good list (brief §3.1): the feed polls, and a transient
    // failure must not blank the page.
    const at = lastGoodAt ? lastGoodAt.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' }) : null;
    loadError.value = at
      ? `Couldn't load the feed. Showing what was loaded at ${at}; retrying every 20 seconds.`
      : 'Couldn\'t load the feed. Retrying every 20 seconds.';
  } finally {
    loading.value = false;
  }
}

async function loadMore() {
  if (loadingMore.value || !hasMore.value || cursor.value === null) return;
  loadingMore.value = true;
  try {
    const data = await api.get(`${props.apiBase}/alerts?${queryString({ before_id: cursor.value })}`);
    const known = new Set(alerts.value.map((a) => a.id));
    alerts.value = [...alerts.value, ...(data.alerts || []).map(normalise).filter((a) => !known.has(a.id))];
    hasMore.value = Boolean(data.has_more);
    cursor.value = data.next_before_id ?? null;
  } catch {
    loadError.value = 'Couldn\'t load more alerts. Try again.';
  } finally {
    loadingMore.value = false;
  }
}

async function ensurePinned() {
  const id = String(props.filters.alert || '');
  if (!id || alerts.value.some((a) => String(a.id) === id)) {
    pinned.value = null;
    return;
  }
  try {
    const data = await api.get(`${props.apiBase}/alerts/${encodeURIComponent(id)}`);
    pinned.value = data.alert ? normalise(data.alert) : null;
    if (pinned.value?.status === 'resolved') showResolved.value = true;
  } catch {
    pinned.value = null;
  }
}

function registerRow(id, el) {
  if (el) rowEls.set(String(id), el);
  else rowEls.delete(String(id));
}

function scrollToHighlight() {
  if (!pendingScroll) return;
  const target = pendingScroll;
  nextTick(() => {
    const comp = rowEls.get(target);
    const el = comp?.$el;
    if (el && typeof el.scrollIntoView === 'function') {
      el.scrollIntoView({ block: 'center', behavior: 'smooth' });
      pendingScroll = '';
    }
  });
}

function replaceRow(updated) {
  if (!updated) return;
  const next = normalise(updated);
  alerts.value = alerts.value.map((a) => (a.id === next.id ? next : a));
  if (pinned.value?.id === next.id) pinned.value = next;
}

async function runRowAction(row, label, fn) {
  const key = String(row.id);
  busy.add(key);
  delete rowErrors[row.id];
  try {
    await fn();
    await load();
  } catch {
    // Inline, never silent (brief §3.1).
    rowErrors[row.id] = `Couldn't ${label}. Try again.`;
  } finally {
    busy.delete(key);
  }
}

function ack(row) {
  if (row.status !== 'open') return;
  runRowAction(row, 'acknowledge', async () => replaceRow((await api.post(`${props.apiBase}/alerts/${row.id}/ack`))?.alert));
}

function resolve(row) {
  if (row.status === 'resolved') return;
  runRowAction(row, 'resolve', async () => replaceRow((await api.post(`${props.apiBase}/alerts/${row.id}/resolve`))?.alert));
}

function cancelSnooze(row) {
  if (!row.snooze) return;
  runRowAction(row, 'cancel the snooze', () => api.post(`${props.apiBase}/suppressions/${row.snooze.id}/revoke`, {}));
}

function openSnooze(row) {
  if (row.status === 'resolved') return;
  snoozeFor.value = row;
}

function openAccept(row) {
  if (row.status === 'resolved' || !row.acceptable) return;
  acceptFor.value = row;
}

async function afterHold() {
  snoozeFor.value = null;
  acceptFor.value = null;
  await load();
}

function toggle(row) {
  const key = String(row.id);
  if (expanded.has(key)) expanded.delete(key);
  else expanded.add(key);
}

// ── keyboard (brief §5, Q11): j / k / Enter / a / s / x / ? ──────────────
function typingOrModal(event) {
  const t = event.target;
  const tag = (t?.tagName || '').toLowerCase();
  if (['input', 'select', 'textarea'].includes(tag) || t?.isContentEditable) return true;
  return !!document.querySelector('[aria-modal="true"]');
}

function focusRow(index) {
  const rows = visibleRows.value;
  if (!rows.length) return;
  const clamped = Math.max(0, Math.min(rows.length - 1, index));
  const id = String(rows[clamped].id);
  focusedId.value = id;
  const el = rowEls.get(id)?.$el;
  if (el) {
    el.focus();
    el.scrollIntoView?.({ block: 'nearest' });
  }
}

function currentRow() {
  const active = document.activeElement;
  const fromFocus = visibleRows.value.find((r) => rowEls.get(String(r.id))?.$el?.contains(active));
  return fromFocus || visibleRows.value.find((r) => String(r.id) === focusedId.value) || null;
}

function onKey(event) {
  if (!props.active || event.ctrlKey || event.metaKey || event.altKey || typingOrModal(event)) return;
  const rows = visibleRows.value;
  const index = rows.findIndex((r) => r === currentRow());
  switch (event.key) {
    case 'j':
      event.preventDefault();
      focusRow(index < 0 ? 0 : index + 1);
      break;
    case 'k':
      event.preventDefault();
      focusRow(index < 0 ? 0 : index - 1);
      break;
    case 'a': {
      const row = currentRow();
      if (row) { event.preventDefault(); ack(row); }
      break;
    }
    case 's': {
      const row = currentRow();
      if (row) { event.preventDefault(); openSnooze(row); }
      break;
    }
    case 'x': {
      const row = currentRow();
      if (row) { event.preventDefault(); openAccept(row); }
      break;
    }
    case '?':
      event.preventDefault();
      helpOpen.value = true;
      break;
    default:
  }
}

defineExpose({ openHelp: () => { helpOpen.value = true; } });

watch(() => [props.filters.status, props.filters.severity, props.filters.server_id, props.filters.rule_type, props.filters.since, props.filters.until], () => {
  cursor.value = null;
  hasMore.value = false;
  alerts.value = [];
  loadedOnce.value = false;
  load();
});

watch(() => props.filters.alert, (id) => {
  if (id) pendingScroll = String(id);
  ensurePinned().then(scrollToHighlight);
});

onMounted(() => {
  if (props.filters.alert) pendingScroll = String(props.filters.alert);
  load();
  pollHandle = setInterval(load, POLL_MS);
  document.addEventListener('keydown', onKey);
});

onBeforeUnmount(() => {
  if (pollHandle) clearInterval(pollHandle);
  document.removeEventListener('keydown', onKey);
});
</script>

<style lang="scss">
.alerts-feed {
  &__toolbar {
    display: flex;
    align-items: center;
    gap: 10px;
    flex-wrap: wrap;
    padding: 12px 16px;
    border-bottom: 1px solid var(--theme-border-soft);
  }

  &__seg {
    display: inline-flex;
    background: var(--theme-border-soft);
    border-radius: var(--theme-radius, 6px);
    padding: 2px;
    gap: 2px;
    max-width: 100%;
    overflow-x: auto;
  }

  &__seg-btn {
    font-size: 11.5px;
    font-weight: 600;
    color: var(--theme-text-secondary);
    padding: 5px 10px;
    border-radius: 4px;
    border: 0;
    background: transparent;
    cursor: pointer;
    white-space: nowrap;
    font-family: inherit;

    em {
      font-style: normal;
      color: var(--theme-text-tertiary);
      font-weight: 500;
      margin-left: 4px;
    }

    &.is-on {
      background: var(--theme-bg-card);
      color: var(--theme-text-primary);
      box-shadow: var(--theme-shadow-card);
    }

    &:focus-visible {
      outline: 2px solid var(--theme-accent-primary);
      outline-offset: 1px;
    }
  }

  &__sel {
    font-size: 11.5px;
    color: var(--theme-text-secondary);
    border: 1px solid var(--theme-border-strong);
    border-radius: var(--theme-radius-sm, 3px);
    padding: 5px 8px;
    background: var(--theme-bg-card);
  }

  &__spacer {
    flex: 1;
  }

  &__note {
    font-size: 11px;
    color: var(--theme-text-tertiary);
  }

  &__scope-chip {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px 16px;
    font-size: 12px;
    color: var(--theme-text-secondary);
    border-bottom: 1px solid var(--theme-border-soft);
  }

  &__error {
    margin: 12px 14px;
    padding: 9px 12px;
    border-radius: var(--theme-radius, 6px);
    background: #fbe1e1;
    border: 1px solid #eeb8b8;
    color: #8a2a2a;
    font-size: 12px;
  }

  &__empty {
    padding: 24px 16px;
    text-align: center;
    color: var(--theme-text-tertiary);
    font-size: 12.5px;
  }

  &__empty-state {
    padding: 26px 16px;
    text-align: center;
    font-size: 12.5px;
    color: var(--theme-text-tertiary);

    h4 {
      margin: 0 0 4px;
      font-size: 13.5px;
      font-weight: 600;
      color: var(--theme-text-primary);
    }

    p {
      margin: 0;
    }
  }

  &__es-glyph {
    width: 34px;
    height: 34px;
    border-radius: 50%;
    background: #e1f3ea;
    color: #1c7a4d;
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0 auto 10px;

    .alerts-icon {
      width: 16px;
      height: 16px;
      stroke-width: 2;
    }
  }

  &__section {
    padding: 9px 16px 7px;
    font-size: 10.5px;
    font-weight: 700;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: var(--theme-text-tertiary);
    background: var(--theme-bg-content);
    border-bottom: 1px solid var(--theme-border-soft);

    span {
      font-weight: 500;
      letter-spacing: 0;
    }
  }

  &__fold {
    padding: 4px 16px;
    border-bottom: 1px solid var(--theme-border-soft);
  }

  &__fold-btn {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    flex-wrap: wrap;
    border: 0;
    background: none;
    padding: 8px 0;
    font-size: 12px;
    color: var(--theme-text-secondary);
    cursor: pointer;
    font-family: inherit;
    text-align: left;
  }

  &__fold-sum {
    color: var(--theme-text-tertiary);
  }

  &__more {
    display: flex;
    justify-content: center;
    padding: 12px 0;
  }

  &__keys {
    margin: 0;
    font-size: 12.5px;

    div {
      display: flex;
      gap: 12px;
      padding: 5px 0;
      border-bottom: 1px dashed var(--theme-border);
    }

    dt {
      width: 110px;
      flex-shrink: 0;
    }

    dd {
      margin: 0;
      color: var(--theme-text-secondary);
    }
  }

  @media (max-width: 600px) {
    &__toolbar {
      padding: 10px 12px;
    }

    &__note {
      display: none;
    }
  }
}
</style>
